import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Button, FormControl, InputLabel, Select, MenuItem,
  Alert, Chip, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TablePagination, Paper, CircularProgress, Stack,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Image as ImageIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { aiMappingApi, categoryTaxonomyApi } from '../../services/api';
import SuggestionChip from './SuggestionChip';
import TypePicker from './TypePicker';
import SampleProducts from './SampleProducts';
import MappingImpact, { impactSentence } from './MappingImpact';
import BulkMapBar, { RowCheckbox, SelectAllCheckbox, canMapToSuggestion } from './BulkMapBar';

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

const SourceIcon = ({ source }) =>
  source === 'image' ? (
    <Tooltip title="Extracted from image"><ImageIcon fontSize="small" color="primary" /></Tooltip>
  ) : (
    <Tooltip title="Extracted from description"><DescriptionIcon fontSize="small" color="secondary" /></Tooltip>
  );

export default function UnmappedCategoryTypesPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [source, setSource] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapDialogRecord, setMapDialogRecord] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [typeOptions, setTypeOptions] = useState([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = { limit: pageSize, offset: page * pageSize };
      if (debouncedSearch) params.search = debouncedSearch;
      if (source) params.source = source;
      const data = await aiMappingApi.getUnmappedCategories(params);
      setRows(data.unmapped_categories || []);
      setTotal(data.total || 0);
      setSelected(new Set());
    } catch (e) {
      setError(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, source]);

  useEffect(() => { load(); }, [load]);

  // The dialog maps to an existing category type only (closed list); the AI suggestion is preselected.
  const handleOpenMap = (row) => {
    setMapDialogRecord(row);
    setSelectedType(row.suggested_normalized_value || null);
    setMapDialogOpen(true);
    setTypesLoading(true);
    Promise.all([categoryTaxonomyApi.listTypes(), categoryTaxonomyApi.listFamilies()])
      .then(([types, fams]) => {
        const familyName = Object.fromEntries((fams.families || []).map(f => [f.id, f.family]));
        setTypeOptions((types.types || []).map(t => ({ value: t.category, family: familyName[t.family_id] || null })));
      })
      .catch(() => setTypeOptions([]))
      .finally(() => setTypesLoading(false));
  };

  const handleCreateMapping = async () => {
    if (!mapDialogRecord || !selectedType) return;
    setActionLoading(true);
    try {
      await aiMappingApi.createCategoryMapping(mapDialogRecord.extracted_category, selectedType);
      setSuccess(`Mapped "${mapDialogRecord.extracted_category}" → "${selectedType}"`);
      setMapDialogOpen(false);
      setMapDialogRecord(null);
      setSelectedType(null);
      load();
    } catch (e) {
      setError(e.message || 'Failed to create mapping');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              size="small"
              label="Search extracted category..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(0); }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Source</InputLabel>
              <Select value={source} label="Source" onChange={(e) => { setSource(e.target.value); setPage(0); }}>
                <MenuItem value=""><em>All sources</em></MenuItem>
                <MenuItem value="description">Description</MenuItem>
                <MenuItem value="image">Image</MenuItem>
              </Select>
            </FormControl>
            {(source || search) && (
              <Button size="small" onClick={() => { setSource(''); setSearch(''); setPage(0); }}>Clear</Button>
            )}
            <Box sx={{ flex: 1 }} />
            <Tooltip title="Refresh"><IconButton onClick={load}><RefreshIcon /></IconButton></Tooltip>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Labels the AI gave products — from the description text (<DescriptionIcon fontSize="inherit" />) or the
            photo (<ImageIcon fontSize="inherit" />) — that have no mapping yet. The description label wins when both are
            mapped; the photo label decides only for products whose description gives no mappable label.
            The retailer's own feed category is not used. Confident AI suggestions are mapped automatically
            every night; the labels left here need a human. Mapping picks from the closed list of category
            types — a new type is added on the “Category Types → Family” tab. Tick rows to accept several
            AI suggestions at once.
          </Alert>

          <BulkMapBar
            rows={rows}
            selected={selected}
            setSelected={setSelected}
            labelOf={(r) => r.extracted_category}
            suggestionOf={(r) => r.suggested_normalized_value}
            mapOne={(r) => aiMappingApi.createCategoryMapping(r.extracted_category, r.suggested_normalized_value)}
            onDone={load}
          />

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"><SelectAllCheckbox rows={rows} selected={selected} setSelected={setSelected} canSelect={canMapToSuggestion} /></TableCell>
                  <TableCell>Extracted Category</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>AI suggestion</TableCell>
                  <TableCell>Products</TableCell>
                  <TableCell><Tooltip title="Products without a category yet / products carrying the label; below: what the rest hold today"><span>Will change</span></Tooltip></TableCell>
                  <TableCell align="right">Occurrences</TableCell>
                  <TableCell>Last seen</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}><CircularProgress /></TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4 }}>No unmapped category types match these filters.</TableCell></TableRow>
                ) : (
                  rows.map(row => (
                    <TableRow key={row.id} hover selected={selected.has(row.id)}>
                      <TableCell padding="checkbox"><RowCheckbox row={row} selected={selected} setSelected={setSelected} canSelect={canMapToSuggestion} /></TableCell>
                      <TableCell><Typography variant="body2" fontWeight="medium">{row.extracted_category}</Typography></TableCell>
                      <TableCell><SourceIcon source={row.source} /></TableCell>
                      <TableCell><SuggestionChip row={row} /></TableCell>
                      <TableCell><SampleProducts products={row.sample_products} /></TableCell>
                      <TableCell><MappingImpact products={row.products} kind="category" /></TableCell>
                      <TableCell align="right">
                        <Chip label={row.occurrence_count} size="small" color={row.occurrence_count >= 10 ? 'warning' : 'default'} variant="outlined" />
                      </TableCell>
                      <TableCell>{row.last_seen_at ? new Date(row.last_seen_at).toLocaleDateString() : '-'}</TableCell>
                      <TableCell align="right">
                        <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => handleOpenMap(row)}>Map</Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component="div"
            count={total}
            rowsPerPage={pageSize}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
          />
        </CardContent>
      </Card>

      <Dialog open={mapDialogOpen} onClose={() => setMapDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Map Category</DialogTitle>
        <DialogContent>
          {mapDialogRecord && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                <Typography variant="subtitle2">
                  Extracted: <Chip label={mapDialogRecord.extracted_category} size="small" color="primary" />
                </Typography>
                <SampleProducts products={mapDialogRecord.sample_products} size={48} />
              </Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {impactSentence(mapDialogRecord.products, 'category')}. Tonight's pass applies the mapping, re-maps included.
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                Seen {mapDialogRecord.occurrence_count} time(s)
                {mapDialogRecord.suggested_normalized_value && (
                  <> · AI suggests <strong>{mapDialogRecord.suggested_normalized_value}</strong> ({Math.round((mapDialogRecord.confidence || 0) * 100)}%)</>
                )}
              </Typography>
              {(mapDialogRecord.suggestions || []).length > 1 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                    The AI gave different answers — pick one, or choose another type below:
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {mapDialogRecord.suggestions.map(a => (
                      <Chip
                        key={a.value}
                        label={`${a.value} · ${Math.round((a.confidence || 0) * 100)}%`}
                        size="small"
                        color={selectedType === a.value ? 'primary' : 'default'}
                        variant={selectedType === a.value ? 'filled' : 'outlined'}
                        onClick={() => setSelectedType(a.value)}
                      />
                    ))}
                  </Stack>
                </Box>
              )}
              <TypePicker
                options={typeOptions}
                value={selectedType}
                onChange={setSelectedType}
                label="Category type"
                loading={typesLoading}
                typesTabName="Category Types → Family"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapDialogOpen(false)} disabled={actionLoading}>Cancel</Button>
          <Button onClick={handleCreateMapping} variant="contained" disabled={!selectedType || actionLoading}>
            {actionLoading ? <CircularProgress size={20} /> : 'Map'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
